import type { QueriesResults, UseQueryResult } from '@tanstack/react-query'

import { useQueries } from '@tanstack/react-query'
import { Fragment } from 'react'
import { MdInfoOutline } from 'react-icons/md'
import { Link } from 'react-router-dom'

import http from '../../lib/http'

import styles from './new-claims-warning.module.css'

export interface ConfigEntry {
  id: string
  name: string
  queryFn: (params: QueryParams) => Promise<number>
  redirectUrl: (params: GetRedirectUrlParams) => string
}

interface QueryParams {
  patientId: string
}

interface GetRedirectUrlParams {
  patientId: string
  clientName: string
}

interface DiagnosesResponse {
  total_filtered: number
}

interface MedicationsResponse {
  total_filtered: number
}

interface ProvidersResponse {
  total: number
}

export let config: ConfigEntry[] = [
  {
    id: 'diagnoses',
    name: 'Diagnoses',
    queryFn: async ({ patientId }) => await http.getRequest(`/patients/${patientId}/claims/diagnoses?size=1&status=new&severity=3&severity=4`).then((response: DiagnosesResponse) => response.total_filtered),
    redirectUrl: ({ patientId, clientName }) => `/patient/${clientName}/${patientId}/assessment/standardmedicaldiagnosis`
  },
  {
    id: 'medications',
    name: 'Medications',
    queryFn: async ({ patientId }) => await http.getRequest(`/patients/${patientId}/claims/medications?size=1&status=new`).then((response: MedicationsResponse) => response.total_filtered),
    redirectUrl: ({ patientId, clientName }) => `/patient/${clientName}/${patientId}/assessment/standardmedications`
  },
  {
    id: 'providers',
    name: 'Providers',
    queryFn: async ({ patientId }) => await http.getRequest(`/patients/${patientId}/claims/providers?size=1&status=new`).then((response: ProvidersResponse) => response.total),
    redirectUrl: ({ patientId, clientName }) => `/patient/${clientName}/${patientId}/assessment/standarddemographicsmedical`
  }
]

interface NewClaimsWarningProps {
  patientId: string
  clientName: string
}

export function getErrorMessage (err: unknown): string {
  if (err instanceof Error) {
    return err.message
  }

  return 'Unknown error'
}

export function useClaimsQuery (patientId: string, clientName: string): QueriesResults<Array<{ entry: ConfigEntry, count: number }>> {
  return useQueries<Array<{entry: ConfigEntry, count: number}>>({
    queries: config.map(entry => ({
      queryKey: [
        'new-claims',
        clientName,
        patientId,
        entry.id
      ],

      async queryFn () {
        let count = await entry.queryFn({ patientId })

        return {
          entry,
          count
        }
      },

      refetchOnWindowFocus: false,

      retry: false,

      placeholderData: {
        entry,
        count: 0
      }
    }))
  })
}

export function NewClaimsWarning ({ clientName, patientId }: NewClaimsWarningProps): JSX.Element | null {
  let queries = useClaimsQuery(patientId, clientName) as Array<UseQueryResult<{entry: ConfigEntry, count: number}>>

  let relevantQueries = queries.filter(query => query.isError || (query.isSuccess && query.data.count > 0))

  if (!relevantQueries.length) {
    return null
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.iconColumn}>
        <MdInfoOutline className={styles.icon} />
      </div>

      <div className={styles.contentColumn}>
        <div className={styles.row}>
          <h2 className={styles.title}>New Claims</h2>
        </div>

        <div className={styles.row}>
          <p className={styles.p}>
            There are new claims that can be added to the patient’s chart or ignored.
            Please use the links below to identify where new claims have been detected
            and either add or ignore them:
          </p>
        </div>

        <div className={styles.row}>
          <div className={styles.grid}>
            {relevantQueries.map((query) => {
              let { entry } = query.data ?? {}

              if (!entry) {
                return null
              }

              return (
                <Fragment
                  key={entry.id}
                >
                  <Link
                    to={entry.redirectUrl({ patientId, clientName })}
                    className={styles.link}
                  >
                    {entry.name}
                  </Link>

                  <span className={styles.count}>
                    {query.isError && getErrorMessage(query.error)}
                    {query.isSuccess && query?.data?.count?.toString(10)}
                  </span>
                </Fragment>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
